import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import moment from 'moment-timezone';
import { Moment } from 'moment';
import { TranslocoService } from '@jsverse/transloco';
import { ShuttelbussService } from '../../services/shuttlebuss.service';
import { Flightinfo, TravelTime, TravelTimeMaker } from '../../models/flightModel';

/**
 * @title Display value autocomplete
 */

@Component({
  selector: 'app-booking-select-arrival',
  templateUrl: './booking-select-arrival.component.html',
  styleUrls: ['./booking-select-arrival.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BokoingSelectArrivalComponent implements OnInit {
  @Input() startDate: Moment = moment();
  @Input() needStartDate: Boolean = true;
  @Input() limitShuttles: Boolean = false;
  @Input() timeZone: string;
  @Output() newItemEvent = new EventEmitter<TravelTime>();

  isLoading = false;
  arrivalflights: Flightinfo[];
  filteredFlightOptions: Observable<Flightinfo[]>;

  flightArrivalForm = this.fb.group({
    flightArrivalDate: [{ value: '' }, Validators.required],
    flightNumber: [{ value: '', disabled: true }, Validators.required]
  });

  get formatter() {
    return Intl.DateTimeFormat("sv-SE", {
      timeZone: this.timeZone,
      hour: "numeric",
      minute: "numeric",
    });
  }

  constructor(
    private fb: UntypedFormBuilder,
    public shuttleBussService: ShuttelbussService,
    public translate: TranslocoService,
  ) { }

  ngOnInit() {
    if (this.needStartDate && this.startDate == null) {
      this.flightArrivalForm.controls.flightArrivalDate.disable();
    }
  }

  ngOnChanges(changes: any) {
    if (this.needStartDate && this.startDate == null) {
      this.flightArrivalForm.controls.flightArrivalDate.disable();
      this.flightArrivalForm.controls.flightNumber.disable();
    }
    else {
      this.flightArrivalForm.controls.flightArrivalDate.enable();
      if (this.flightArrivalForm.controls.flightArrivalDate.valid) {
        this.flightArrivalForm.controls.flightArrivalDate.enable();
      }
    }
  }

  displayArrivalFlight(flight: Flightinfo): string {
    return flight && flight.flightId ? flight.flightId + ' ' + flight.departureAirportSwedish + ' kl:' + moment.tz(flight.arrivalTime.scheduled, this.timeZone).format('HH:mm') + ' ' + flight.codeShareData : '';
  }

  resetForm() {
    this.flightArrivalForm.controls.flightArrivalDate.setValue('');
    this.resetArrivalFlight();
    this.flightArrivalForm.controls.flightNumber.disable();
  }

  resetArrivalFlight() {
    this.newItemEvent.emit(null);
    this.flightArrivalForm.controls.flightNumber.setValue('');
    this.filteredFlightOptions = this.flightArrivalForm.controls.flightNumber.valueChanges.pipe(
      startWith(''),
      map(value => this._filterArrivalFlightsAutocomplete(value))
    );
    this.flightArrivalForm.controls.flightArrivalDate.enable();
    this.flightArrivalForm.controls.flightNumber.enable();
  }

  getArrivalFlightsByDate(date: any) {
    this.flightArrivalForm.controls.flightNumber.setValue('');
    this.isLoading = true;

    this.shuttleBussService.getFlights(date.value.format('YYYY-MM-DD'), 'arrival').subscribe(
      flightInfo => {
        const now = moment().tz(this.timeZone);
        this.arrivalflights = flightInfo
          .filter(
            flight => {
              const arrivalTime = moment.tz(flight.arrivalTime.scheduled, this.timeZone);
              const hour = arrivalTime.hour();
              const minute = arrivalTime.minute();
              if (this.limitShuttles) {
                return (
                  flight.locationAndStatus.flightLegStatus !== 'DEL' &&
                  flight.locationAndStatus.flightLegStatus !== 'Deleted' &&
                  flight.locationAndStatus.flightLegStatus !== 'Departed' &&
                  arrivalTime.isAfter(now) &&
                  (hour > 4 || (hour === 4 && minute >= 0)) &&
                  (hour < 23 || (hour === 23 && minute <= 30))
                );
              }
              else {
                return (
                  flight.locationAndStatus.flightLegStatus !== 'DEL' &&
                  flight.locationAndStatus.flightLegStatus !== 'Deleted' &&
                  flight.locationAndStatus.flightLegStatus !== 'Departed' &&
                  arrivalTime.isAfter(now)
                );
              }
            }
          )
          .sort((a, b) => (a.arrivalTime.scheduled < b.arrivalTime.scheduled ? -1 : 1));
        this.isLoading = false;
        this.resetArrivalFlight();
      },
      error => {
        this.isLoading = false;
        console.error('error', error);
      }
    );
  }

  private _filterArrivalFlightsAutocomplete(value: string): Flightinfo[] {
    const filterValue = typeof value === 'string' ? value.toLowerCase() : '';
    return this.arrivalflights?.filter(
      option =>
        option.flightId?.toLowerCase()?.includes(filterValue) ||
        option.departureAirportSwedish?.toLowerCase()?.includes(filterValue) ||
        option.departureAirportEnglish?.toLowerCase()?.includes(filterValue) ||
        option.codeShareData
          ?.toString()
          ?.toLowerCase()
          ?.includes(filterValue)
    );
  }

  onArrivalFlightChange(event) {
    this.newItemEvent.emit(this.getTravelTime(event.option.value));
  }

  private getTravelTime(flight: Flightinfo): TravelTime {
    return TravelTimeMaker.create({ flight: flight })
  }

  displayTime(value: Date) {
    return this.formatter.format(new Date(value));
  }
}
